import { ChangeDetectionStrategy, Component, inject, Inject, Input, OnDestroy, OnInit } from '@angular/core';
import { FormArray, FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { ConfigConstants, ConfigService, GroupedMatSerNumber, isDefined, MatSerNumber } from '@evorait/pace-core';
import { LogInstanceToken, LogInterface } from '@evorait/pace-logging';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { select, Store } from '@ngrx/store';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    distinctUntilKeyChanged,
    filter,
    map,
    mergeMap,
    pairwise,
    startWith,
    switchMap,
    take,
    takeUntil,
    withLatestFrom,
} from 'rxjs/operators';
import {
    updateDescription,
    updateFunctionalElementRemarks,
    updateFunctionalElementSelection,
    updateMaterialNumber,
    updateMaterialSerialNumber,
    updateSerialNumber,
} from '../../store/actions/functional-elements.actions';
import {
    getForm,
    getFormCheckpointByXmlIdFactory,
    getFormFilters,
    getFormFunctionalElementByIdFactory,
    getFormParticipantCopyReadonly,
    getFormRadioOptionsByXmlIdFactory,
    getFormSelectedParticipantCopyId,
    getFormTypeFilters,
    getOptionPresentNoSelectedForFunctionalElement,
    isCosimaFunctionalElementFromTrainingChecklistReadOnly,
} from '../../store/selectors/form.selector';
import {
    getEquipmentSetForNotificationFactory,
    getGroupedEquipmentSetForNotificationFactory,
} from '../../store/selectors/list.selector';
import { FormsState } from '../../store/state/pace-forms.state';
import { Checkpoint } from '../../types/checkpoint';
import { CHOICE_TYPE } from '../../types/choice';
import { ContentId } from '../../types/content-block';
import { FormFilters } from '../../types/form-filter';
import { FormTypeFilters } from '../../types/form-type-filters';
import { FormsConstants } from '../../types/forms-constants';
import { FUNCTIONAL_ELEMENT_TYPE } from '../../types/functional-element';
import { MaterialSerialNumber } from '../../types/material-serial-number';
import { ParticipantCopyId } from '../../types/participant-copy';
import { RADIO_OPTION_TYPE, RadioOption } from '../../types/radio-option';
import { isAstoriaFunctionalElementVisible, isCosimaFunctionalElementVisible } from '../../utilities/answer-utilities';
import { FormUtilities } from '../../utilities/form-utilities';

@Component({
    selector: 'pace-forms-material-serial-number',
    templateUrl: './material-serial-number.component.html',
    styleUrls: ['./material-serial-number.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MaterialSerialNumberComponent implements OnInit, OnDestroy {
    private readonly configService = inject(ConfigService);
    @Input()
    public id: ContentId;

    @Input()
    public parentForm: FormArray | undefined;

    private readOnly$: Observable<boolean>;
    public materialSerialNumber$: Observable<MaterialSerialNumber>;
    public controllingCheckpoint$: Observable<Checkpoint | undefined>;
    public options$: Observable<RadioOption[] | undefined>;
    public selectedParticipant$: Observable<ParticipantCopyId>;
    public typeFilters$: Observable<FormTypeFilters>;
    public formFilters$: Observable<FormFilters>;
    public elementTypeLabel$: Observable<string>;
    public visible$: Observable<boolean>;
    public displayDescription$: Observable<boolean>;
    public displayRemarksField$: Observable<boolean>;
    public displayNotApplicableCheckbox$: Observable<boolean>;
    public optionPresentNoSelected$: Observable<boolean>;
    public displayHistoryButton$: Observable<boolean>;

    public groupedMaterialSuggestion$: Observable<GroupedMatSerNumberDescription[]>;
    public serialNumberSuggestion$: Observable<MatSerNumberDescription[]>;
    private autoUpdateMaterialSerialNumberSelection: Subject<MatSerNumberDescription>;

    private destroy$ = new Subject<void>();

    public materialSerialNumberGroup: FormGroup;
    public materialInputField: FormControl;
    public serialInputField: FormControl;
    public descriptionInputField: FormControl;
    public checkboxControl: FormControl;
    public radioGroup: FormGroup;
    public radioControl: FormControl;
    public remarksControl: FormControl;

    public faCheck = faCheck;
    public constructor(
        @Inject(LogInstanceToken) private readonly log: LogInterface,
        private readonly store: Store<FormsState>,
        private readonly formBuilder: FormBuilder,
    ) {
        this.typeFilters$ = store.select(getFormTypeFilters);
        this.formFilters$ = store.select(getFormFilters);
        this.selectedParticipant$ = store.select(getFormSelectedParticipantCopyId);
    }

    public ngOnInit(): void {
        this.autoUpdateMaterialSerialNumberSelection = new Subject();

        this.radioControl = this.formBuilder.control('');
        this.remarksControl = this.formBuilder.control('');
        this.radioGroup = this.formBuilder.group({ radioControl: this.radioControl });
        this.materialInputField = this.formBuilder.control('');
        this.serialInputField = this.formBuilder.control('');
        this.descriptionInputField = this.formBuilder.control('');
        this.checkboxControl = this.formBuilder.control(false);

        if (this.configService.getMatSerTypeaheadToggle()) {
            const form$ = this.store
                .select(getForm)
                .pipe(
                    distinctUntilKeyChanged('formId', (prevFormId, nextFormId) =>
                        FormUtilities.compare(prevFormId, nextFormId),
                    ),
                );

            this.groupedMaterialSuggestion$ = form$.pipe(
                switchMap((form) =>
                    this.store
                        .select(
                            getGroupedEquipmentSetForNotificationFactory({
                                notification: form.notification,
                            }),
                        )
                        .pipe(
                            map((groupedMaterialSuggestion) => {
                                return groupedMaterialSuggestion.map((groupedMaterial) => {
                                    return {
                                        ...groupedMaterial,
                                        sernrDescription: `${groupedMaterial.matnr} - ${groupedMaterial.description}`,
                                    };
                                });
                            }),
                        ),
                ),
                takeUntil(this.destroy$),
            );

            this.serialNumberSuggestion$ = combineLatest([
                form$.pipe(
                    switchMap((form) =>
                        this.store
                            .select(
                                getEquipmentSetForNotificationFactory({
                                    notification: form.notification,
                                }),
                            )
                            .pipe(
                                map((equipmentSet) => {
                                    return equipmentSet.map((equipment) => {
                                        return {
                                            ...equipment,
                                            sernrDescription: `${equipment.sernr} - ${equipment.description}`,
                                        };
                                    });
                                }),
                            ),
                    ),
                ),
                this.materialInputField.valueChanges.pipe(startWith('')),
            ]).pipe(
                map(([equipmentSet, materialNumberInput]) => {
                    if (isDefined(materialNumberInput) && materialNumberInput !== '') {
                        return equipmentSet.filter((entry) => entry.matnr === materialNumberInput);
                    }
                    return equipmentSet;
                }),
            );
        } else {
            this.groupedMaterialSuggestion$ = of([]);
            this.serialNumberSuggestion$ = of([]);
        }

        this.materialSerialNumberGroup = this.formBuilder.group({
            remarks: this.remarksControl,
            radioGroup: this.radioGroup,
            material: this.materialInputField,
            serial: this.serialInputField,
            description: this.descriptionInputField,
            checkbox: this.checkboxControl,
        });

        if (this.parentForm) {
            const parent = this.parentForm;
            Promise.resolve(undefined)
                .then(() => {
                    parent.push(this.materialSerialNumberGroup);
                })
                .catch((error: Error) =>
                    this.log.error(
                        'MaterialSerialNumberComponent: ngOnInit: Error in parentForm.push: ',
                        JSON.stringify(error),
                    ),
                );
        }

        const optionPresentNoSelectedSelector = getOptionPresentNoSelectedForFunctionalElement({ contentId: this.id });
        this.optionPresentNoSelected$ = this.store.select(optionPresentNoSelectedSelector);

        this.materialSerialNumber$ = this.store
            .select(getFormFunctionalElementByIdFactory<MaterialSerialNumber>({ id: this.id }))
            .pipe(filter(isDefined));

        this.displayHistoryButton$ = this.materialSerialNumber$.pipe(
            map(
                (materialSerialNumber) =>
                    isDefined(materialSerialNumber.uniqueId) &&
                    materialSerialNumber.uniqueId.length > 0 &&
                    this.configService.getEnableFormsHistoricalValues(),
            ),
        );

        this.readOnly$ = combineLatest([
            this.store.select(getFormParticipantCopyReadonly),
            this.store.select(isCosimaFunctionalElementFromTrainingChecklistReadOnly({ id: this.id })),
        ]).pipe(map(([readOnly, isCosimaReadOnly]) => readOnly || isCosimaReadOnly));

        this.elementTypeLabel$ = combineLatest([
            this.materialSerialNumber$.pipe(
                map((m) => m.elementType),
                filter(isDefined),
            ),
            this.store.select(getFormTypeFilters),
        ]).pipe(map(([elementType, filters]) => FormUtilities.getFilterLabel(elementType, filters)));

        this.displayDescription$ = this.materialSerialNumber$.pipe(
            map(
                (materialSerialNumber) =>
                    materialSerialNumber.type === FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO_WITH_DESC,
            ),
        );

        this.controllingCheckpoint$ = this.materialSerialNumber$.pipe(
            mergeMap((materialSerialNumber) =>
                materialSerialNumber.controllingCheckpoint.length > 0
                    ? this.store.pipe(
                          select(
                              getFormCheckpointByXmlIdFactory({ xmlId: materialSerialNumber.controllingCheckpoint }),
                          ),
                          take(1),
                      )
                    : of(undefined),
            ),
        );

        this.options$ = this.controllingCheckpoint$.pipe(
            switchMap((checkpoint) =>
                typeof checkpoint !== 'undefined'
                    ? this.store.select(getFormRadioOptionsByXmlIdFactory({ xmlId: checkpoint.xmlId }))
                    : of(undefined),
            ),
        );

        this.visible$ = combineLatest([
            this.materialSerialNumber$,
            this.controllingCheckpoint$,
            this.options$,
            this.typeFilters$,
            this.formFilters$,
            this.selectedParticipant$,
        ]).pipe(
            map(([materialSerialNumber, checkpoint, options, typeFilters, formFilters, selectedParticipant]) => {
                const isMasterCopy = selectedParticipant === FormsConstants.MASTER_PARTICIPANT_ID;
                // The master displays everything. In a participant copy, we display only content that was filled out in
                // the current session and not loaded from XML.
                let visible = isMasterCopy || !materialSerialNumber.contentLoadedFromXml;

                // choices are only available in cosima
                if (materialSerialNumber.choices) {
                    // in participant copies, only content that was answered with "Yes" in this session is displayed
                    const selectedInMaster = Object.values(materialSerialNumber.choices).find(
                        (c) => c?.selectedInMaster,
                    );
                    const yesInMaster =
                        typeof selectedInMaster !== 'undefined' &&
                        selectedInMaster.type === CHOICE_TYPE.CHOICE_POSITIVE;
                    const visibleInParticipantCopy = !materialSerialNumber.contentLoadedFromXml && yesInMaster;

                    // filters apply both to master and participant copies
                    const visibleByFilter = isCosimaFunctionalElementVisible(
                        materialSerialNumber,
                        typeFilters,
                        formFilters,
                        materialSerialNumber.elementType,
                    );
                    visible = (isMasterCopy || visibleInParticipantCopy) && visibleByFilter;
                } else {
                    // In addition to the rule above, a controlling checkpoint can control the visibility by either the type
                    // of the checkpoint and a selected matching filter, or by the chosen answer: In a participant copy, we
                    // display only values answered with "yes".
                    const visibleByFilter = isAstoriaFunctionalElementVisible(
                        materialSerialNumber,
                        checkpoint?.elementType,
                        typeFilters,
                        formFilters,
                        checkpoint?.xmlId ? { [checkpoint.xmlId]: options } : {},
                    );
                    visible = visibleByFilter && visible;
                    if (typeof checkpoint !== 'undefined' && typeof options !== 'undefined') {
                        const selectedInMaster = options.find((o) => o.selectedInMaster);
                        const yesInMaster =
                            typeof selectedInMaster !== 'undefined' &&
                            selectedInMaster.optionType === RADIO_OPTION_TYPE.YES &&
                            !selectedInMaster.contentLoadedFromXml;
                        visible = visible && (isMasterCopy || yesInMaster);
                    }
                }

                return visible;
            }),
        );

        combineLatest([this.materialSerialNumber$, this.readOnly$])
            .pipe(withLatestFrom(this.optionPresentNoSelected$), takeUntil(this.destroy$))
            .subscribe(([[materialSerialNumber, readOnly], optionPresentNoSelected]) => {
                const disableInputFields =
                    materialSerialNumber.controllingCheckpoint.length === 0 && this.checkboxControl.value;
                this.materialInputField.patchValue(materialSerialNumber.materialValue);
                this.serialInputField.patchValue(materialSerialNumber.serialValue);
                this.descriptionInputField.patchValue(materialSerialNumber.descriptionValue, { emitEvent: false });
                this.materialInputField[
                    materialSerialNumber.disabled || readOnly || disableInputFields ? 'disable' : 'enable'
                ]({
                    emitEvent: false,
                });
                this.serialInputField[
                    materialSerialNumber.disabled || readOnly || disableInputFields ? 'disable' : 'enable'
                ]({
                    emitEvent: false,
                });
                this.descriptionInputField[
                    materialSerialNumber.disabled || readOnly || disableInputFields ? 'disable' : 'enable'
                ]({
                    emitEvent: false,
                });

                if (materialSerialNumber.controllingCheckpoint.length === 0) {
                    this.checkboxControl.patchValue(disableInputFields, { emitEvent: false });
                    this.checkboxControl[materialSerialNumber.disabled || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });
                }

                let shouldDisableRemarksField = materialSerialNumber.disabled || readOnly || disableInputFields;

                // cosima choices
                if (materialSerialNumber.choices) {
                    const selectedChoice = Object.values(materialSerialNumber.choices).find(
                        (choice) => choice?.selected,
                    );

                    if (isDefined(selectedChoice)) {
                        this.radioControl.patchValue(selectedChoice.xmlId);
                    } else if (
                        materialSerialNumber.materialValue.length === 0 &&
                        materialSerialNumber.serialValue.length === 0
                    ) {
                        this.radioControl.reset('');
                    }

                    this.radioControl[optionPresentNoSelected || readOnly ? 'disable' : 'enable']({
                        emitEvent: false,
                    });

                    this.remarksControl.patchValue(materialSerialNumber.remarks);

                    shouldDisableRemarksField =
                        shouldDisableRemarksField || selectedChoice?.type !== CHOICE_TYPE.CHOICE_NEGATIVE;
                }

                this.remarksControl[shouldDisableRemarksField ? 'disable' : 'enable']({
                    emitEvent: false,
                });
            });

        this.displayRemarksField$ = this.materialSerialNumber$.pipe(
            map(
                (materialSerialNumber) =>
                    !!(materialSerialNumber.remarks?.length || materialSerialNumber.choices?.choiceNegative?.selected),
            ),
        );

        this.displayNotApplicableCheckbox$ = this.materialSerialNumber$.pipe(
            map(
                (materialSerialNumber) =>
                    materialSerialNumber.controllingCheckpoint.length === 0 &&
                    typeof materialSerialNumber.choices === undefined,
            ),
        );

        this.autoUpdateMaterialSerialNumberSelection
            .pipe(withLatestFrom(this.materialSerialNumber$), takeUntil(this.destroy$))
            .subscribe(([matSerSelection, materialSerialNumber]) => {
                this.store.dispatch(
                    updateMaterialSerialNumber({
                        id: materialSerialNumber.id,
                        serialXmlId: materialSerialNumber.serialXmlId,
                        materialXmlId: materialSerialNumber.materialXmlId,
                        descriptionXmlId: materialSerialNumber.descriptionXmlId,
                        descriptionValue: materialSerialNumber.descriptionValue,
                        materialValue: matSerSelection.matnr,
                        serialValue:
                            matSerSelection.sernr === '' ? materialSerialNumber.serialValue : matSerSelection.sernr,
                    }),
                );
            });

        this.materialInputField.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.materialSerialNumber$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, materialSerialNumber]: [string, MaterialSerialNumber]) => {
                this.store.dispatch(
                    updateMaterialNumber({
                        xmlId: materialSerialNumber.materialXmlId,
                        id: materialSerialNumber.id,
                        newValue,
                    }),
                );
            });

        this.serialInputField.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.materialSerialNumber$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, materialSerialNumber]: [string, MaterialSerialNumber]) => {
                this.store.dispatch(
                    updateSerialNumber({
                        xmlId: materialSerialNumber.serialXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.descriptionInputField.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                // we use take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.materialSerialNumber$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, materialSerialNumber]: [string, MaterialSerialNumber]) => {
                // If there is no description in the functional element, we can't update it. In that case the UI will
                // not display the input field for description and the user will not be able to directly edit the value,
                // but using "Fill all" they still can set the field value when the controlling checkpoint is updated.
                if (materialSerialNumber.type === FUNCTIONAL_ELEMENT_TYPE.MATERIAL_SERIAL_NO) {
                    return;
                }
                this.store.dispatch(
                    updateDescription({
                        xmlId: materialSerialNumber.descriptionXmlId,
                        id: this.id,
                        newValue,
                    }),
                );
            });

        this.checkboxControl.valueChanges
            .pipe(
                // the combination of startWith and pairWise enables us to access the previous and the current value in
                // the observable
                startWith(false),
                pairwise(),
                // we use take(1) because we are only interested in static properties of materialSerialNumber$
                withLatestFrom(this.materialSerialNumber$.pipe(take(1))),
                takeUntil(this.destroy$),
            )
            .subscribe(([[previousChecked, currentChecked], materialSerialNumber]) => {
                if (!previousChecked && currentChecked) {
                    this.store.dispatch(
                        updateMaterialSerialNumber({
                            id: this.id,
                            materialXmlId: materialSerialNumber.materialXmlId,
                            materialValue: FormsConstants.NA,
                            serialXmlId: materialSerialNumber.serialXmlId,
                            serialValue: FormsConstants.NA,
                            descriptionXmlId: materialSerialNumber.descriptionXmlId,
                            descriptionValue: FormsConstants.NA,
                        }),
                    );
                } else if (previousChecked && !currentChecked) {
                    this.store.dispatch(
                        updateMaterialSerialNumber({
                            id: this.id,
                            materialXmlId: materialSerialNumber.materialXmlId,
                            materialValue: '',
                            serialXmlId: materialSerialNumber.serialXmlId,
                            serialValue: '',
                            descriptionXmlId: materialSerialNumber.descriptionXmlId,
                            descriptionValue: '',
                        }),
                    );
                }
                this.materialInputField[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
                this.serialInputField[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
                this.descriptionInputField[currentChecked ? 'disable' : 'enable']({ emitEvent: false });
            });

        this.radioControl.valueChanges
            .pipe(distinctUntilChanged(), withLatestFrom(this.materialSerialNumber$), takeUntil(this.destroy$))
            .subscribe(([value, materialSerialNumber]: [string, MaterialSerialNumber]) => {
                this.store.dispatch(
                    updateFunctionalElementSelection({
                        id: materialSerialNumber.id,

                        selectedChoiceXmlId: value === null ? undefined : value,
                    }),
                );
            });

        this.remarksControl.valueChanges
            .pipe(
                debounceTime(ConfigConstants.debounceTimeMs),
                distinctUntilChanged(),
                withLatestFrom(this.materialSerialNumber$),
                takeUntil(this.destroy$),
            )
            .subscribe(([newValue, materialSerialNumber]: [string, MaterialSerialNumber]) => {
                if (!isDefined(materialSerialNumber.xmlId)) {
                    throw new Error(
                        `MaterialSerialNumberComponent: remarksControl valueChanges: material-serial-number element has no xml Id!`,
                    );
                }
                if (materialSerialNumber.remarksXmlId) {
                    this.store.dispatch(
                        updateFunctionalElementRemarks({
                            id: materialSerialNumber.id,
                            // using ! because remarksXmlId is guaranteed to be there in Cosima. It is only undefined in Astoria.
                            xmlId: materialSerialNumber.remarksXmlId,
                            newValue,
                        }),
                    );
                }
            });
    }

    public ngOnDestroy(): void {
        // remove form controls from parent
        if (this.parentForm) {
            const index = this.parentForm.controls.findIndex((control) => control === this.materialSerialNumberGroup);
            this.parentForm.removeAt(index);
        }
        this.destroy$.next();
    }

    public reset(): void {
        this.radioControl.reset();
    }

    public autofillEquipment(selectedEquipment: MatSerNumberDescription | GroupedMatSerNumberDescription): void {
        let serialNumber = '';
        if (typeof selectedEquipment.sernr === 'string') {
            // serialNumbers always have exactly one materialNumber as match
            serialNumber = selectedEquipment.sernr;
        } else if (Array.isArray(selectedEquipment.sernr)) {
            // selectedMaterial has only one corresponding serial number
            serialNumber = selectedEquipment.sernr[0];
        }
        this.autoUpdateMaterialSerialNumberSelection.next({ ...selectedEquipment, sernr: serialNumber });
    }
}

type MatSerNumberDescription = Readonly<{
    sernrDescription: string;
}> &
    MatSerNumber;

type GroupedMatSerNumberDescription = Readonly<{
    sernrDescription: string;
}> &
    GroupedMatSerNumber;
